import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import logisticsApi from '@utils/logisticsApi';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { IconPaperclip, IconChevron } from '@components/icons';

export default function RequestDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    logisticsApi.get(`/requests/${id}`).then(({ data }) => setData(data)).catch(() => setLoadError(true));
  }, [id]);

  const loadAttachments = useCallback(() => {
    logisticsApi.get('/attachments', { params: { entityType: 'request', entityId: id } })
      .then(({ data }) => setAttachments(data.attachments))
      .catch(() => {});
  }, [id]);

  useEffect(() => { load(); loadAttachments(); }, [load, loadAttachments]);

  async function upload(e) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('entityType', 'request');
      form.append('entityId', id);
      form.append('attachmentType', 'PHOTO');
      await logisticsApi.post('/attachments', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null);
      loadAttachments();
    } catch (err) {
      setError(err.response?.data?.message || t('logisticsRequests.detail.errUpload'));
    } finally {
      setBusy(false);
    }
  }

  async function download(att) {
    const { data } = await logisticsApi.get(`/attachments/${att.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.original_filename || 'anexo';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader title={t('logisticsRequests.detail.title')} />
      <div className="px-5 md:px-0 pb-6 space-y-4">
        <Link to="/requests" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-glk-dark -mt-1 mb-1">
          <IconChevron size={14} className="rotate-180" /> {t('logisticsRequests.detail.back')}
        </Link>

        {loadError ? (
          <div className="card p-8 text-center">
            <p className="text-sm text-red-600 mb-3">{t('logisticsRequests.detail.errLoad')}</p>
            <button onClick={load} className="btn-ghost text-xs">{t('logisticsRequests.detail.retry')}</button>
          </div>
        ) : !data ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : (
          <>
            <div className="card p-5">
              <p className="text-xs font-medium text-glk-blue uppercase tracking-wide mb-1">
                {t(`logisticsRequests.status.${data.request.status}`) || data.request.status}
              </p>
              <p className="text-sm text-glk-dark whitespace-pre-wrap">{data.request.justification || '—'}</p>
              {data.request.rejection_reason && (
                <p className="text-sm text-red-600 mt-2">{data.request.rejection_reason}</p>
              )}
            </div>

            <div className="card p-5">
              <h2 className="text-sm font-semibold text-glk-dark mb-3">{t('logisticsRequests.product')}</h2>
              <div className="space-y-2">
                {data.lines.map((l) => (
                  <div key={l.id} className="flex justify-between text-sm">
                    <span className="text-glk-dark">{l.product_name}</span>
                    <span className="text-gray-400">{l.quantity}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card p-5">
              <h2 className="text-sm font-semibold text-glk-dark mb-3 flex items-center gap-2">
                <IconPaperclip size={16} /> {t('logisticsRequests.detail.attachments')}
              </h2>

              {error && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

              {attachments.length === 0 ? (
                <p className="text-sm text-gray-400 mb-3">{t('logisticsRequests.detail.noAttachments')}</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {attachments.map((a) => (
                    <button key={a.id} onClick={() => download(a)}
                      className="flex items-center justify-between w-full text-left text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">
                      <span className="truncate">{a.original_filename}</span>
                      <span className="text-gray-400 text-xs shrink-0 ml-2">
                        {new Date(a.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT')}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={upload} className="flex gap-2">
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="input flex-1" />
                <motion.button type="submit" disabled={busy || !file} whileTap={{ scale: 0.98 }} className="btn-primary shrink-0">
                  {t('logisticsRequests.detail.upload')}
                </motion.button>
              </form>
            </div>
          </>
        )}
      </div>
    </>
  );
}
